// Route: /about — публичная страница «О проекте».
// Тактильный экран «Картотека»: спокойный заголовок, контент на панели e2.
// Контент — i18n namespace `legal`, ключ `about`. Обе темы.
//
// Маршрут темы ①②③ страница НЕ пересказывает своим текстом: его рисует
// `MethodExplainer` — единственный на приложение компонент «Метод»
// (PRODUCT-NARRATIVE §10.3). Второй экземпляр этих трёх названий в markdown =
// регресс, поэтому в `about.body` их нет, а есть отдельная панель с компонентом
// и ссылка на полный разбор `/method`.

import { cn, ElevatedPanel, NARROW_PAGE_SHELL, PANEL_PAD } from "@studyhub/ui";
import { IconArrowRight, IconInfoCircle } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { MethodExplainer } from "@/components/how-it-works/method-explainer";
import { LEGAL_PROSE_CLASS } from "@/components/legal/legal-prose";
import { PageMeta } from "@/components/seo/PageMeta";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";

export const Route = createFileRoute("/about")({
	component: AboutPage,
	pendingComponent: PagePendingFallback,
});

function AboutPage(): React.ReactElement {
	const { t } = useTranslation("legal");

	return (
		<>
			<PageMeta title={t("about.title")} description={t("about.lead")} path="/about" />
			<main className={NARROW_PAGE_SHELL}>
				<div className="flex items-start gap-3">
					<div className="flex size-10 shrink-0 items-center justify-center rounded-[var(--radius-tile)] bg-primary/10 [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]">
						<IconInfoCircle size={20} className="text-primary" stroke={1.75} />
					</div>
					<div className="min-w-0">
						<h1 className="text-2xl font-semibold tracking-tight lg:text-3xl">
							{t("about.title")}
						</h1>
						<p className="mt-1 text-sm text-muted-foreground">{t("about.lead")}</p>
					</div>
				</div>

				{/* Метод — первым блоком: он отвечает на главный вопрос страницы
				    («как вы учите»), а остальной текст его разворачивает. */}
				<ElevatedPanel className={cn(PANEL_PAD, "mt-6 overflow-hidden")}>
					<h2 className="text-base font-semibold tracking-tight">{t("about.methodTitle")}</h2>
					<MethodExplainer variant="compact" className="mt-3" />
					<Link
						to="/method"
						preload="intent"
						className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary cursor-pointer hover:underline"
					>
						{t("about.methodLink")}
						<IconArrowRight size={16} stroke={1.75} aria-hidden />
					</Link>
				</ElevatedPanel>

				<ElevatedPanel className={cn(PANEL_PAD, "mt-4 overflow-hidden")}>
					<article className={LEGAL_PROSE_CLASS}>
						<ReactMarkdown>{t("about.body")}</ReactMarkdown>
					</article>
				</ElevatedPanel>
			</main>
		</>
	);
}
